import { deleteCookie } from '../modules/common/services/cookies.service';
import {
    destroyAdvancedFeatureIndications,
    initAdvancedFeaturesIndications,
    renderAdvancedFeatureBadge,
    renderAdvancedFeatureIndication,
} from '../modules/advancedFeatures/data/advancedFeatures.service';
import { submitForm } from '../modules/contactUs/data/contactUs.service';
import { FormType } from '../modules/contactUs/data/constants';
import { logoutAndRedirect, LogoutOptions } from '../modules/common/services/api.service';
import * as launchEvents from './events/launch.events';
import contactUsModalEvents from '../modules/common/events/contactUsModal.events';
import headerEvents from '../modules/common/events/header.events';
import breadCrumbsEvents from '../modules/common/events/breadcrumbs.events';
import exclusionsModalEvents from './events/exclusions.events';
import translationsModalEvents from './events/translations.events';
import importProjectModalEvents from './events/importProject.events';
import notificationsEvents from '../modules/notifications/data/notifications.events';
import betaSuggestionModalEvents from '../modules/common/events/betaSuggestion.events';
import { init, sendGtagEvent } from '../modules/gtag/gtag.module';
import initExclusions from '../modules/exclusions/exclusions.module';
import initTranslations from '../modules/translations/translations.module';
import initImportProject from '../modules/importProject/importProject.module';
import { ACCOUNT_NOTIFICATION_TYPE } from '../modules/accountNotifications/data/constants';
import accountNotificationsService from '../modules/accountNotifications/data/accountNotifications.service';
import { getSurveyParametersById } from '../modules/launch/data/service';
import { IAdvancedFeatureIndicatorProps } from '../modules/advancedFeatures/components/AdvancedFeatureIndicator/AdvancedFeatureIndicator';
import { ErrorCallback, SuccessCallback } from '../modules/common/services/network.service';
import {
    ContactUsFormValues,
    DefaultOptions,
    InitOptions,
    SalesforceConfig,
    SelectOption,
} from '../modules/common/models/common.models';
import { initExternalModule } from './externalModule';
import * as maxDiffModalEvents from './events/maxDiff.events';
import * as operationalQuotasModalEvents from './events/operationalQuotas.events';
import WalkmeModule from "../modules/walkme/walkme.module";

const defaultOptions: DefaultOptions = {
    advancedFeatures: {
        renderIndications: true,
    },
};

let options: InitOptions = {};

export const getGtag = () => {
    return {
        init,
        sendGtagEvent
    };
};

export const getWalkMe = () => {
    return {
        init: WalkmeModule.init,
        setWalkMeTemplateId: WalkmeModule.setWalkMeTemplateId,
        CUSTOM_SURVEY_ID: WalkmeModule.CUSTOM_SURVEY_ID,
    };
};

export const initOptions = (config: InitOptions) => {
    options = {
        ...defaultOptions,
        ...config,
    };
};

export const handleRedirectToLogin = () => {
    window.tcp.redirectToLogin = (redirectUrlAfterLogin: string) => {
        deleteCookie('AuthToken', options.domain);
        if (redirectUrlAfterLogin) {
            window.location.href = options.loginUrl + '?rurl=' + redirectUrlAfterLogin;
        } else if (options.loginUrl) {
            window.location.href = options.loginUrl;
        }
    };
};

export const handleAdvancedFeaturesIndications = () => {
    if (options.advancedFeatures && options.advancedFeatures.renderIndications) {
        initAdvancedFeaturesIndications(options.baseUrl, false);
    }
};

export const getBreadcrumbs = () => {
    return breadCrumbsEvents;
};

export const getAdvancedFeatures = () => {
    return {
        renderIndication: (el: HTMLElement, config?: IAdvancedFeatureIndicatorProps) =>
            renderAdvancedFeatureIndication(el, config, options.baseUrl),
        renderAllIndicationsOnPage: () => initAdvancedFeaturesIndications(options.baseUrl),
        destroyAllIndicationsOnPage: destroyAdvancedFeatureIndications,
        renderBadge: renderAdvancedFeatureBadge
    };
};

export const getContactUs = () => {
    return {
        showModal: contactUsModalEvents.showContactUsModal,
        hideModal: contactUsModalEvents.hideContactUsModal,
        submit: (
            values: ContactUsFormValues,
            helpOption: SelectOption,
            onSuccess: SuccessCallback,
            onError: ErrorCallback,
            salesForceConfig: SalesforceConfig
        ) => submitForm(
            values,
            helpOption,
            onSuccess,
            onError,
            options.baseUrl,
            salesForceConfig,
        ),
        modalTypes: FormType,
    };
};

export const getHeader = () => {
    return {
        events: headerEvents.headerEvents,
        hideCreateMenu: headerEvents.hideCreateMenu,
        notifyHeaderMinimized: headerEvents.notifyHeaderMinimized,
    };
};

export const getUser = () => {
    return {
        logout: (handleSignOut?: () => void) => logoutAndRedirect(options as LogoutOptions, handleSignOut),
    };
};

export const getLaunch = () => {
    return {
        init: initExternalModule('launch'),
        getSurveyParametersById: getSurveyParametersById,
        showModal: launchEvents.showLaunchModal,
        hideModal: launchEvents.hideLaunchModal,
    };
};

export const getExclusions = () => {
    return {
        init: initExclusions,
        showModal: exclusionsModalEvents.showExclusionsModal,
        hideModal: exclusionsModalEvents.hideExclusionsModal,
        loadModal: exclusionsModalEvents.loadExclusionsModal,
    };
};

export const getTranslations = () => {
    return {
        init: initTranslations,
        showModal: translationsModalEvents.showTranslationsModal,
        hideModal: translationsModalEvents.hideTranslationsModal,
        loadModal: translationsModalEvents.loadTranslationsModal,
    };
};

export const getImportProject = () => {
    return {
        init: initImportProject,
        showModal: importProjectModalEvents.showImportProjectModal,
        hideModal: importProjectModalEvents.hideImportProjectModal,
    };
};

export const getNotifications = () => {
    return {
        addBanner: notificationsEvents.addBanner,
        accountNotificationTypes: ACCOUNT_NOTIFICATION_TYPE,
        addAccountNotifications: accountNotificationsService.handleNewNotifications,
        onAccountNotificationsServiceReady: (callback: Function) => accountNotificationsService.addOnReadyCallback(callback),
    };
};

export const getMaxDiff = () => {
    return {
        init: initExternalModule('maxDiff'),
        showModal: maxDiffModalEvents.showMaxDiffModal,
        hideModal: maxDiffModalEvents.hideMaxDiffModal,
    };
};

export const getOperationalQuotas = () => {
    return {
        init: initExternalModule('operationalQuotas'),
        showModal: operationalQuotasModalEvents.showOperationalQuotasModal,
        hideModal: operationalQuotasModalEvents.hideOperationalQuotasModal,
    };
};

export const getBetaSuggestion = () => {
    return {
        showModal: betaSuggestionModalEvents.showBetaSuggestionModal,
        hideModal: betaSuggestionModalEvents.hideBetaSuggestionModal,
    };
};
